import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BsCalendar3, BsClock } from "react-icons/bs";
import blogsData from "../../data/blogs.json";

const PER_PAGE = 6;

function BlogCard({ blog }) {
  const navigate = useNavigate();
  const { slug, title, category, date, readTime, image, excerpt } = blog;
  const d = new Date(date).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });

  return (
    <article
      className="blog-card"
      onClick={() => navigate(`/blog/${slug}`)}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/blog/${slug}`)}
      aria-label={`Read: ${title}`}
    >
      <div className="blog-img-box">
        <img src={image} alt={title} loading="lazy" />
        <span className="blog-cat-badge">{category}</span>
      </div>
      <div className="blog-body">
        <h3 className="blog-title">{title}</h3>
        <p className="blog-excerpt">{excerpt}</p>
        <div className="blog-meta-row">
          <span className="blog-meta-item"><BsCalendar3 />{d}</span>
          <span className="blog-meta-item"><BsClock />{readTime}</span>
        </div>
        <div className="blog-read-link">
          <span>Read Article</span>
          <span className="blog-arrow">→</span>
        </div>
      </div>
    </article>
  );
}

function BlogsSection() {
  const [cat, setCat]   = useState("All");
  const [page, setPage] = useState(1);

  const categories = useMemo(
    () => ["All", ...new Set(blogsData.map((b) => b.category))],
    []
  );
  const filtered = useMemo(
    () => cat === "All" ? blogsData : blogsData.filter((b) => b.category === cat),
    [cat]
  );
  const total = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function selectCat(c) { setCat(c); setPage(1); }
  function changePage(p) {
    setPage(p);
    document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <section id="blog" className="portfolio-section alt">
      <div className="section-container">
        <span className="section-label">Writing</span>
        <h2 className="section-title">My <span>Blog</span></h2>
        <p className="section-subtitle">
          Thoughts on web development, system design, and everything in between.
        </p>
        <div className="section-divider" />

        {/* Category Filters */}
        <div className="blog-filters">
          {categories.map((c) => (
            <button
              key={c}
              className={`filter-btn${cat === c ? " active" : ""}`}
              onClick={() => selectCat(c)}
              type="button"
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="blog-grid">
          {paged.length > 0
            ? paged.map((b) => <BlogCard key={b.id} blog={b} />)
            : (
              <p style={{
                color: "var(--text-3)", gridColumn: "1/-1",
                textAlign: "center", padding: "40px 0",
              }}>
                No posts in this category.
              </p>
            )
          }
        </div>

        {/* Pagination */}
        {total > 1 && (
          <div className="blog-pagination">
            <button
              className="page-btn nav-btn"
              disabled={page === 1}
              onClick={() => changePage(page - 1)}
            >
              ← Prev
            </button>
            {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                className={`page-btn${page === n ? " active" : ""}`}
                onClick={() => changePage(n)}
              >
                {n}
              </button>
            ))}
            <button
              className="page-btn nav-btn"
              disabled={page === total}
              onClick={() => changePage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default BlogsSection;
